import { Checkbox, FormControlLabel, Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { ReprintCidFormVM, SearchPersonVM } from 'front-office/view-models/reprint-cid/form';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import Table from 'shell/components/shared/table';
import { headCells } from 'shell/constants/person-list';
import { noop } from 'shell/utils/noop';
import { PersonVM } from 'shell/view-models/search-person';
import { Visibility } from '@mui/icons-material';

interface SearchPersonProps {
  values?: SearchPersonVM | null;
  onChange: (key: keyof SearchPersonVM, value: SearchPersonVM[keyof SearchPersonVM]) => void;
  errors?: FormikErrors<SearchPersonVM>;
  touched?: FormikTouched<SearchPersonVM>;
  disabled?: boolean;
}

const SearchPersonComponent = ({
  values,
  onChange,
  errors,
  touched,
  disabled = false,
}: SearchPersonProps): React.ReactElement => {
  const formContext = useFormikContext<ReprintCidFormVM>();

  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  const onVisibilityPress = useEventCallback((item: PersonVM) => {
    formContext.setFieldValue('person', {
      ...formContext.values.person,
      list: values?.list || [], // mantenere lista in caso di errore si può ripetere la chiamata
      fiscalCode: item.fiscalCode || '',
    });

    // rieffettuare ricerca per CF
    formContext.handleSubmit();
  });

  const body: Array<PersonVM> = useMemo(() => {
    if ((values?.list || []).length === 0) {
      return [];
    }

    return values?.list?.map((item) => ({
      ...item,
      action: (
        <IconButton
          aria-label="visualizza"
          component="span"
          onClick={() => onVisibilityPress(item)}
        >
          <Visibility />
        </IconButton>
      ),
    })) as Array<PersonVM>;
  }, [values?.list, onVisibilityPress]);

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          id="fiscalCode"
          fullWidth
          onChange={(event) => {
            onChange('fiscalCode', event?.target.value);
          }}
          value={values?.fiscalCode?.toUpperCase() || ''}
          inputProps={{ maxLength: '16' }}
          error={touched?.fiscalCode && Boolean(errors?.fiscalCode)}
          helperText={errors?.fiscalCode}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          label="Cognome"
          aria-label="Cognome"
          id="surname"
          fullWidth
          onChange={(event) => {
            onChange('surname', event?.target.value);
          }}
          value={values?.surname || ''}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="withoutSurname"
              checked={values?.withoutSurname || false}
              onChange={() => onChange('withoutSurname', !values?.withoutSurname)}
              inputProps={{ 'aria-label': 'Persona senza cognome' }}
              disabled={disabled}
            />
          }
          label="Persona senza cognome"
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          label="Nome"
          aria-label="Nome"
          id="name"
          fullWidth
          onChange={(event) => {
            onChange('name', event?.target.value);
          }}
          value={values?.name || ''}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="withoutName"
              checked={values?.withoutName || false}
              onChange={() => onChange('withoutName', !values?.withoutName)}
              inputProps={{ 'aria-label': 'Persona senza nome' }}
              disabled={disabled}
            />
          }
          label="Persona senza nome"
        />
      </Grid>
      {/* <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
        <Grid item xs={12} md={3}>
          <PmiButton
            type="submit"
            color="cdmBrandThird"
            variant="outlined"
            fullWidth
            disabled={disabled}
          >
            Cerca
          </PmiButton>
        </Grid>
      </Grid> */}
      {(body || []).length > 1 ? (
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista Persone"
            page={tableProps.page}
            rowsPerPage={tableProps.rowsPerPage}
            body={body}
            count={body?.length || 0}
            head={headCells}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            onSort={noop}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

SearchPersonComponent.displayName = 'SearchPerson form component';

const SearchPerson = memo(SearchPersonComponent);

export default SearchPerson;
